import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Calendar as CalendarIcon, 
  Clock, 
  Trash2, 
  Plus, 
  Users, 
  Lock,
  LogOut,
  Loader2,
  Check,
  X,
  FileText,
  ClipboardList,
  UserCircle,
  TrendingUp,
  MessageCircle,
  Sparkles,
  Database,
  Rocket,
  AlertTriangle
} from "lucide-react";
import { format, addDays } from "date-fns";
import { AdminStats, IntakeFormsTab, QuizLeadsTab, PortalClientsTab, SessionBriefRow } from "@/components/AdminInsights";
import { AdminMarketing } from "@/components/AdminMarketing";
import { AdminBookingsTab } from "@/components/AdminBookings";
import { AdminReviewsTab } from "@/components/AdminReviews";
import { AdminMeetingsCard } from "@/components/AdminMeetings";
import { AdminBlackoutsCard } from "@/components/AdminBlackouts";
import { GoogleSignInButton, AuthDivider } from "@/components/GoogleSignInButton";
import { AdminRecapsTab } from "@/components/AdminRecaps";
import { AdminResearchCard } from "@/components/AdminResearch";
import { AdminCaseBaseTab } from "@/components/AdminCaseBase";
import { AdminEmailHealthCard, EmailBounceBanner } from "@/components/AdminEmailHealth";
import { AdminPaymentsCard } from "@/components/AdminPayments";
import { AdminLaunchTab } from "@/components/AdminLaunch";

const TIMEZONES = [
  "UTC", "Europe/London", "Europe/Dublin", "Europe/Lisbon", "Europe/Madrid", "Europe/Paris", "Europe/Brussels",
  "Europe/Amsterdam", "Europe/Berlin", "Europe/Zurich", "Europe/Vienna", "Europe/Rome", "Europe/Stockholm",
  "Europe/Oslo", "Europe/Copenhagen", "Europe/Helsinki", "Europe/Warsaw", "Europe/Prague", "Europe/Budapest",
  "Europe/Athens", "Europe/Bucharest", "Europe/Istanbul", "America/New_York", "America/Chicago", "America/Denver",
  "America/Los_Angeles", "America/Toronto", "America/Sao_Paulo", "Asia/Dubai", "Asia/Kolkata", "Asia/Singapore",
  "Asia/Hong_Kong", "Asia/Tokyo", "Australia/Sydney", "Pacific/Auckland",
];
const BROWSER_TZ = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
const TZ_OPTIONS = TIMEZONES.includes(BROWSER_TZ) ? TIMEZONES : [BROWSER_TZ, ...TIMEZONES];

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ADMIN_TOKEN_KEY = "bbc_admin_token";
const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem(ADMIN_TOKEN_KEY)}` } });
const CLIENTS_PAGE_SIZE = 50;
const RESET_CATEGORY_OPTIONS = [
  { key: "bookings", label: "Bookings & revenue" },
  { key: "clients", label: "Portal clients (incidents, reports, chats)" },
  { key: "quiz", label: "Quiz leads" },
  { key: "intake", label: "Intake forms" },
  { key: "marketing", label: "Marketing analytics" },
  { key: "cases", label: "Clinic-collected cases" },
  { key: "logs", label: "Session briefs/recaps & email logs" },
];
const ALL_RESET_KEYS = RESET_CATEGORY_OPTIONS.map((c) => c.key);

const AdminPanel = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(!!localStorage.getItem(ADMIN_TOKEN_KEY));
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  
  // Availability state
  const [selectedDates, setSelectedDates] = useState([]);
  const [startTime, setStartTime] = useState("09:00");
  const [endTime, setEndTime] = useState("17:00");
  const [slotDuration, setSlotDuration] = useState(60);
  const [availabilitySlots, setAvailabilitySlots] = useState([]);
  
  // Bookings state
  const [bookings, setBookings] = useState([]);

  // Insight state
  const [stats, setStats] = useState(null);
  const [intakeForms, setIntakeForms] = useState([]);
  const [quizLeads, setQuizLeads] = useState([]);
  const [portalClients, setPortalClients] = useState([]);
  const [portalClientsTotal, setPortalClientsTotal] = useState(0);
  const [clientSearch, setClientSearch] = useState("");
  const [debouncedClientSearch, setDebouncedClientSearch] = useState("");
  const [clientPage, setClientPage] = useState(0);

  // Case-file add-on links
  const [caseFileLinks, setCaseFileLinks] = useState({});
  const [caseFileLoading, setCaseFileLoading] = useState(null);

  // Session prep briefs
  const [briefs, setBriefs] = useState({});
  const [openBrief, setOpenBrief] = useState(null);
  const [briefLoading, setBriefLoading] = useState(null);

  // Danger zone — dashboard reset
  const [resetConfirm, setResetConfirm] = useState("");
  const [resetLoading, setResetLoading] = useState(false);
  const [resetTargets, setResetTargets] = useState(ALL_RESET_KEYS);
  const [resetHistory, setResetHistory] = useState([]);

  const handleLogin = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/admin/login`, { password });
      localStorage.setItem(ADMIN_TOKEN_KEY, res.data.token);
      setIsAuthenticated(true);
      toast.success("Login successful");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid password");
    } finally {
      setLoading(false);
    }
  };

  const caseFileLink = async (booking) => {
    setCaseFileLoading(booking.id);
    try {
      const res = await axios.post(`${API}/admin/case-file-link/${booking.id}`, {}, authHeaders());
      setCaseFileLinks((c) => ({ ...c, [booking.id]: res.data.payment_url }));
      try {
        await navigator.clipboard.writeText(res.data.payment_url);
        toast.success("€175 case-file payment link copied — send it to the client");
      } catch {
        toast.success("Payment link ready below");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not create the payment link");
    } finally {
      setCaseFileLoading(null);
    }
  };

  const toggleBrief = async (booking) => {
    if (openBrief === booking.id) {
      setOpenBrief(null);
      return;
    }
    setOpenBrief(booking.id);
    if (briefs[booking.id]) return;
    setBriefLoading(booking.id);
    try {
      const res = await axios.get(`${API}/admin/session-brief/${booking.id}`, {
        ...authHeaders(),
        validateStatus: (status) => status === 200 || status === 404,
      });
      if (res.status === 404) {
        setBriefLoading(null);
        await generateBrief(booking, false);
        return;
      }
      setBriefs((b) => ({ ...b, [booking.id]: res.data }));
    } catch (error) {
      toast.error("Could not load the brief");
    } finally {
      setBriefLoading(null);
    }
  };

  const generateBrief = async (booking, email = false) => {
    setBriefLoading(booking.id);
    setOpenBrief(booking.id);
    try {
      const res = await axios.post(
        `${API}/admin/session-brief/${booking.id}?email=${email}`,
        {},
        authHeaders()
      );
      setBriefs((b) => ({ ...b, [booking.id]: res.data }));
      if (email) {
        toast.success(res.data.emailed_at ? "Brief emailed to you" : "Brief ready (email could not be delivered)");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not build the brief");
    } finally {
      setBriefLoading(null);
    }
  };

  const handle401 = (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem(ADMIN_TOKEN_KEY);
      setIsAuthenticated(false);
      toast.error("Your admin session expired. Please sign in again.");
    } else {
      console.error("Error fetching data:", error);
    }
  };

  const refreshAvailability = async () => {
    try {
      const res = await axios.get(`${API}/availability`, authHeaders());
      setAvailabilitySlots(res.data);
    } catch (error) {
      handle401(error);
    }
  };

  const fetchData = () => {
    // Fire each request independently so the visible availability list paints as
    // soon as its (small, fast) payload resolves, instead of waiting on the
    // heaviest endpoint (portal-clients ~0.7MB) inside a single Promise.all.
    refreshAvailability();
    axios.get(`${API}/bookings`, authHeaders()).then((r) => setBookings(r.data)).catch(handle401);
    axios.get(`${API}/admin/stats`, authHeaders()).then((r) => setStats(r.data)).catch(handle401);
    axios.get(`${API}/admin/intake-forms`, authHeaders()).then((r) => setIntakeForms(r.data)).catch(handle401);
    axios.get(`${API}/quiz-results`, authHeaders()).then((r) => setQuizLeads(r.data)).catch(handle401);
    axios
      .get(`${API}/availability/schedule`, authHeaders())
      .then((r) => {
        if (r.data?.weekdays) setSchedule({ timezone: BROWSER_TZ, ...r.data });
      })
      .catch(handle401);
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchData();
      fetchResetHistory();
    }
  }, [isAuthenticated]);

  const fetchPortalClients = (page, search) => {
    axios
      .get(`${API}/admin/portal-clients`, {
        ...authHeaders(),
        params: { limit: CLIENTS_PAGE_SIZE, skip: page * CLIENTS_PAGE_SIZE, search },
      })
      .then((r) => {
        setPortalClients(r.data.clients);
        setPortalClientsTotal(r.data.total);
      })
      .catch(handle401);
  };

  // Debounce the client search box so we don't hit the API on every keystroke.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedClientSearch(clientSearch), 400);
    return () => clearTimeout(t);
  }, [clientSearch]);

  // A new search always starts back on the first page.
  useEffect(() => {
    setClientPage(0);
  }, [debouncedClientSearch]);

  // Load the current page of portal clients whenever page or search changes.
  useEffect(() => {
    if (isAuthenticated) fetchPortalClients(clientPage, debouncedClientSearch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, clientPage, debouncedClientSearch]);

  const handleCreateSlots = async () => {
    if (selectedDates.length === 0) {
      toast.error("Please select at least one date");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/admin/availability/bulk`, {
        dates: selectedDates.map(d => format(d, "yyyy-MM-dd")),
        start_time: startTime,
        end_time: endTime,
        slot_duration_minutes: slotDuration
      }, authHeaders());
      toast.success("Availability slots created");
      setSelectedDates([]);
      refreshAvailability();
    } catch (error) {
      toast.error("Failed to create slots");
    } finally {
      setLoading(false);
    }
  };

  const [schedule, setSchedule] = useState({
    weekdays: [1, 2, 3],
    start_time: "09:00",
    end_time: "17:00",
    slot_duration_minutes: 60,
    weeks_ahead: 8,
    timezone: BROWSER_TZ,
    enabled: true,
  });
  const [savingSchedule, setSavingSchedule] = useState(false);

  const toggleWeekday = (day) =>
    setSchedule((prev) => ({
      ...prev,
      weekdays: prev.weekdays.includes(day)
        ? prev.weekdays.filter((d) => d !== day)
        : [...prev.weekdays, day].sort((a, b) => a - b),
    }));

  const saveSchedule = async () => {
    if (!schedule.weekdays.length) {
      toast.error("Pick at least one weekday");
      return;
    }
    setSavingSchedule(true);
    try {
      const res = await axios.put(`${API}/availability/schedule`, schedule, authHeaders());
      toast.success(`Schedule saved — ${res.data.created} slots created across ${res.data.dates} days`);
      refreshAvailability();
    } catch (error) {
      toast.error("Failed to save the schedule");
    } finally {
      setSavingSchedule(false);
    }
  };

  const handleDeleteSlot = async (slotId) => {
    try {
      await axios.delete(`${API}/admin/availability/${slotId}`, authHeaders());
      toast.success("Slot deleted");
      refreshAvailability();
    } catch (error) {
      toast.error("Failed to delete slot");
    }
  };

  const handleDeleteAllSlots = async () => {
    if (!window.confirm("Are you sure you want to delete ALL availability slots?")) return;
    
    try {
      await axios.delete(`${API}/admin/availability`, authHeaders());
      toast.success("All slots deleted");
      refreshAvailability();
    } catch (error) {
      toast.error("Failed to delete slots");
    }
  };

  const fetchResetHistory = () => {
    axios
      .get(`${API}/admin/dashboard-resets`, authHeaders())
      .then((r) => setResetHistory(r.data?.resets || []))
      .catch(() => {});
  };

  const toggleResetTarget = (key) =>
    setResetTargets((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));

  const handleResetDashboard = async () => {
    if (resetConfirm !== "RESET" || resetTargets.length === 0) return;
    if (!window.confirm("This permanently deletes the selected data and cannot be undone. Continue?")) return;
    setResetLoading(true);
    try {
      const res = await axios.post(
        `${API}/admin/reset-dashboard`,
        { confirm: resetConfirm, targets: resetTargets },
        authHeaders()
      );
      toast.success(`Dashboard reset — ${(res.data?.total_deleted ?? 0).toLocaleString()} records cleared`);
      setResetConfirm("");
      setResetTargets(ALL_RESET_KEYS);
      fetchData();
      fetchResetHistory();
      setClientPage(0);
      setClientSearch("");
      fetchPortalClients(0, "");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Reset failed");
    } finally {
      setResetLoading(false);
    }
  };


  const handleClearDay = async (date) => {
    if (!window.confirm(
      `Delete all open slots on ${date}? Booked sessions are kept. The day is also blocked so the weekly ` +
      `schedule won't refill it — you can re-open it later under the Blocked dates section.`
    )) return;
    try {
      const res = await axios.delete(`${API}/admin/availability/day/${date}`, authHeaders());
      toast.success(res.data?.message || "Day cleared");
      refreshAvailability();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to clear day");
    }
  };

  // Group slots by date
  const slotsByDate = availabilitySlots.reduce((acc, slot) => {
    if (!acc[slot.date]) acc[slot.date] = [];
    acc[slot.date].push(slot);
    return acc;
  }, {});

  // "At a glance" summary of currently-open availability (future, unbooked, unblocked).
  const nowTs = new Date();
  const openFutureSlots = availabilitySlots
    .filter((s) => !s.is_booked && !s.blocked)
    .map((s) => ({ ...s, _dt: new Date(`${s.local_date || s.date}T${s.local_start_time || s.start_time}:00`) }))
    .filter((s) => !isNaN(s._dt) && s._dt >= nowTs)
    .sort((a, b) => a._dt - b._dt);
  const nextOpenSlot = openFutureSlots[0] || null;
  const openSlotCount = openFutureSlots.length;
  const daysCovered = new Set(openFutureSlots.map((s) => s.local_date || s.date)).size;
  const coveredUntil = openFutureSlots.length ? openFutureSlots[openFutureSlots.length - 1]._dt : null;

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#020617] flex items-center justify-center px-6">
        <div className="max-w-md w-full">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-slate-800 border border-slate-700 mb-4">
              <Lock className="w-8 h-8 text-[#D4AF37]" />
            </div>
            <h1 className="font-serif text-2xl text-white">Admin Access</h1>
            <p className="text-slate-400 text-sm mt-2">Sign in with Google or the admin password</p>
          </div>

          <div className="bg-slate-900/50 border border-slate-800 p-6">
            <GoogleSignInButton returnPath="/admin" testid="admin-google-btn" />
            <AuthDivider text="or use the admin password" />
            <div className="space-y-4">
              <div>
                <Label htmlFor="password" className="text-slate-300">Password</Label>
                <Input
                  id="password"
                  type="password"
                  data-testid="admin-password-input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleLogin()}
                  placeholder="Enter admin password"
                  className="bg-slate-950 border-slate-800 text-white placeholder:text-slate-600 h-12 rounded-none mt-2"
                />
              </div>
              <Button
                data-testid="admin-login-btn"
                onClick={handleLogin}
                disabled={loading}
                className="w-full bg-[#D4AF37] text-slate-950 hover:bg-[#B5952F] rounded-none h-12"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Login"}
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#020617]">
      {/* Header */}
      <header className="bg-slate-900/50 border-b border-slate-800 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="font-serif text-xl text-white">
              Bad Boss <span className="text-[#D4AF37]">Clinic</span>
            </h1>
            <p className="text-slate-400 text-sm">Admin Panel</p>
          </div>
          <Button
            variant="ghost"
            onClick={() => {
              localStorage.removeItem(ADMIN_TOKEN_KEY);
              setIsAuthenticated(false);
            }}
            data-testid="admin-logout-btn"
            className="text-slate-400 hover:text-white"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <EmailBounceBanner />
        <AdminStats stats={stats} />
        <Tabs defaultValue="availability" className="space-y-6">
          <TabsList className="bg-slate-900 border border-slate-800 p-1 rounded-none flex flex-nowrap md:flex-wrap h-auto overflow-x-auto no-scrollbar justify-start">
            <TabsTrigger
              value="launch"
              data-testid="admin-tab-launch"
              className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-slate-950 rounded-none px-4 md:px-6 shrink-0 whitespace-nowrap"
            >
              <Rocket className="w-4 h-4 mr-2" />
              Launch
            </TabsTrigger>
            <TabsTrigger 
              value="availability" 
              data-testid="admin-tab-availability"
              className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-slate-950 rounded-none px-4 md:px-6 shrink-0 whitespace-nowrap"
            >
              <CalendarIcon className="w-4 h-4 mr-2" />
              Availability
            </TabsTrigger>
            <TabsTrigger 
              value="bookings"
              data-testid="admin-tab-bookings"
              className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-slate-950 rounded-none px-4 md:px-6 shrink-0 whitespace-nowrap"
            >
              <Users className="w-4 h-4 mr-2" />
              Bookings
            </TabsTrigger>
            <TabsTrigger 
              value="intake"
              data-testid="admin-tab-intake"
              className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-slate-950 rounded-none px-4 md:px-6 shrink-0 whitespace-nowrap"
            >
              <FileText className="w-4 h-4 mr-2" />
              Intake Forms
            </TabsTrigger>
            <TabsTrigger 
              value="quiz"
              data-testid="admin-tab-quiz"
              className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-slate-950 rounded-none px-4 md:px-6 shrink-0 whitespace-nowrap"
            >
              <ClipboardList className="w-4 h-4 mr-2" />
              Quiz Leads
            </TabsTrigger>
            <TabsTrigger 
              value="portal"
              data-testid="admin-tab-portal"
              className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-slate-950 rounded-none px-4 md:px-6 shrink-0 whitespace-nowrap"
            >
              <UserCircle className="w-4 h-4 mr-2" />
              Portal Clients
            </TabsTrigger>
            <TabsTrigger 
              value="marketing"
              data-testid="admin-tab-marketing"
              className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-slate-950 rounded-none px-4 md:px-6 shrink-0 whitespace-nowrap"
            >
              <TrendingUp className="w-4 h-4 mr-2" />
              Marketing
            </TabsTrigger>
            <TabsTrigger 
              value="recaps"
              data-testid="admin-tab-recaps"
              className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-slate-950 rounded-none px-4 md:px-6 shrink-0 whitespace-nowrap"
            >
              <Sparkles className="w-4 h-4 mr-2" />
              Recaps
            </TabsTrigger>
            <TabsTrigger 
              value="reviews"
              data-testid="admin-tab-reviews"
              className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-slate-950 rounded-none px-4 md:px-6 shrink-0 whitespace-nowrap"
            >
              <MessageCircle className="w-4 h-4 mr-2" />
              Reviews
            </TabsTrigger>
            <TabsTrigger 
              value="casebase"
              data-testid="admin-tab-casebase"
              className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-slate-950 rounded-none px-4 md:px-6 shrink-0 whitespace-nowrap"
            >
              <Database className="w-4 h-4 mr-2" />
              Case Base
            </TabsTrigger>
          </TabsList>

          <TabsContent value="launch">
            <AdminLaunchTab />
            <div className="mt-6 border border-red-500/30 bg-red-500/[0.04] p-6" data-testid="danger-zone-reset">
              <h2 className="font-serif text-xl text-white mb-2 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-red-400" /> Danger zone
              </h2>
              <p className="text-slate-300 text-sm mb-1">
                Reset the dashboard — permanently deletes the selected data below.
              </p>
              <p className="text-slate-500 text-xs mb-4">
                Always kept: your research dataset, availability &amp; schedule, settings and published reviews.
                This cannot be undone.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 mb-5" data-testid="reset-categories">
                {RESET_CATEGORY_OPTIONS.map((cat) => (
                  <label
                    key={cat.key}
                    className="flex items-center gap-3 py-1.5 cursor-pointer text-slate-300 text-sm select-none"
                  >
                    <input
                      type="checkbox"
                      checked={resetTargets.includes(cat.key)}
                      onChange={() => toggleResetTarget(cat.key)}
                      data-testid={`reset-cat-${cat.key}`}
                      className="w-4 h-4 accent-red-500 bg-slate-900 border-slate-600"
                    />
                    {cat.label}
                  </label>
                ))}
              </div>
              <div className="flex flex-wrap gap-3 mb-5 text-[11px] uppercase tracking-widest">
                <button
                  type="button"
                  onClick={() => setResetTargets(ALL_RESET_KEYS)}
                  data-testid="reset-select-all"
                  className="text-slate-400 hover:text-white transition-colors"
                >
                  Select all
                </button>
                <span className="text-slate-700">·</span>
                <button
                  type="button"
                  onClick={() => setResetTargets([])}
                  data-testid="reset-select-none"
                  className="text-slate-400 hover:text-white transition-colors"
                >
                  Clear selection
                </button>
              </div>

              <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                <Input
                  value={resetConfirm}
                  onChange={(e) => setResetConfirm(e.target.value)}
                  placeholder="Type RESET to confirm"
                  data-testid="reset-confirm-input"
                  className="sm:max-w-xs bg-slate-900 border-slate-700 text-white rounded-none"
                />
                <Button
                  onClick={handleResetDashboard}
                  disabled={resetConfirm !== "RESET" || resetTargets.length === 0 || resetLoading}
                  data-testid="reset-dashboard-btn"
                  className="bg-red-600 hover:bg-red-700 text-white rounded-none disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {resetLoading ? "Resetting…" : "Reset dashboard data"}
                </Button>
              </div>

              {resetHistory.length > 0 && (
                <div className="mt-6 pt-5 border-t border-slate-800" data-testid="reset-history">
                  <p className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-3">Recent resets</p>
                  <ul className="space-y-2">
                    {resetHistory.map((r) => (
                      <li key={r.id} className="text-slate-400 text-xs flex flex-wrap items-center gap-x-2">
                        <span className="text-slate-300">{format(new Date(r.at), "d MMM yyyy, HH:mm")}</span>
                        <span className="text-slate-600">·</span>
                        <span>{(r.total_deleted ?? 0).toLocaleString()} records</span>
                        <span className="text-slate-600">·</span>
                        <span className="text-slate-500">{(r.targets || []).join(", ")}</span>
                        <span className="text-slate-600">·</span>
                        <span className="text-slate-500">by {r.by}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </TabsContent>
          <TabsContent value="casebase">
            <AdminCaseBaseTab />
          </TabsContent>
          <TabsContent value="recaps">
            <AdminRecapsTab />
          </TabsContent>
          <TabsContent value="reviews">
            <AdminReviewsTab />
          </TabsContent>
          <TabsContent value="marketing" className="space-y-6">
            <AdminPaymentsCard />
            <AdminEmailHealthCard />
            <AdminMarketing />
          </TabsContent>
          <TabsContent value="intake">
            <IntakeFormsTab forms={intakeForms} />
          </TabsContent>
          <TabsContent value="quiz">
            <QuizLeadsTab results={quizLeads} />
          </TabsContent>
          <TabsContent value="portal" className="space-y-6">
            <AdminResearchCard />
            <PortalClientsTab
              clients={portalClients}
              total={portalClientsTotal}
              page={clientPage}
              pageSize={CLIENTS_PAGE_SIZE}
              search={clientSearch}
              onSearchChange={setClientSearch}
              onPrev={() => setClientPage((p) => Math.max(0, p - 1))}
              onNext={() => setClientPage((p) => p + 1)}
            />
          </TabsContent>

          {/* Availability Tab */}
          <TabsContent value="availability" className="space-y-6">
            <div className="bg-slate-900/50 border border-slate-800 p-6" data-testid="availability-summary">
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <div data-testid="avail-next-open">
                  <p className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2">Next open slot</p>
                  <p className="text-white text-lg font-serif">{nextOpenSlot ? format(nextOpenSlot._dt, "EEE d MMM") : "—"}</p>
                  <p className="text-[#D4AF37] text-xs mt-1 truncate">
                    {nextOpenSlot
                      ? `${format(nextOpenSlot._dt, "HH:mm")}${nextOpenSlot.timezone ? ` · ${nextOpenSlot.timezone}` : ""}`
                      : "No open slots"}
                  </p>
                </div>
                <div data-testid="avail-open-count">
                  <p className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2">Open slots</p>
                  <p className="text-white text-lg font-serif">{openSlotCount}</p>
                  <p className="text-slate-500 text-xs mt-1">bookable now</p>
                </div>
                <div data-testid="avail-days-covered">
                  <p className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2">Days covered</p>
                  <p className="text-white text-lg font-serif">{daysCovered}</p>
                  <p className="text-slate-500 text-xs mt-1">with openings</p>
                </div>
                <div data-testid="avail-covered-until">
                  <p className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2">Covered until</p>
                  <p className="text-white text-lg font-serif">{coveredUntil ? format(coveredUntil, "d MMM") : "—"}</p>
                  <p className="text-slate-500 text-xs mt-1">{coveredUntil ? format(coveredUntil, "yyyy") : ""}</p>
                </div>
              </div>
            </div>
            <AdminMeetingsCard />
            <AdminBlackoutsCard onChange={fetchData} />
            <div className="bg-slate-900/50 border border-slate-800 p-6" data-testid="weekly-schedule-card">
              <h2 className="font-serif text-xl text-white mb-2 flex items-center gap-2">
                <CalendarIcon className="w-5 h-5 text-[#D4AF37]" />
                Weekly Schedule
              </h2>
              <p className="text-slate-400 text-sm mb-6">
                Set your recurring hours once, in your own timezone. Slots are created for the whole horizon and
                topped up automatically twice a day, so clients always see several weeks of availability — each in
                their own local time.
              </p>

              <div className="grid lg:grid-cols-[1fr,auto] gap-8">
                <div className="space-y-6">
                  <div>
                    <Label className="text-slate-300 mb-3 block">Working days</Label>
                    <div className="flex flex-wrap gap-2" data-testid="schedule-weekdays">
                      {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((label, day) => (
                        <button
                          key={label}
                          type="button"
                          data-testid={`schedule-weekday-${day}`}
                          onClick={() => toggleWeekday(day)}
                          className={`px-4 py-2 border text-xs uppercase tracking-widest transition-colors ${
                            schedule.weekdays.includes(day)
                              ? "border-[#D4AF37] text-[#D4AF37]"
                              : "border-slate-800 text-slate-500 hover:border-slate-700"
                          }`}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-4 gap-4">
                    <div>
                      <Label className="text-slate-300">From</Label>
                      <Input
                        type="time"
                        data-testid="schedule-start-time"
                        value={schedule.start_time}
                        onChange={(e) => setSchedule({ ...schedule, start_time: e.target.value })}
                        className="bg-slate-950 border-slate-800 text-white h-12 rounded-none mt-2"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-300">To</Label>
                      <Input
                        type="time"
                        data-testid="schedule-end-time"
                        value={schedule.end_time}
                        onChange={(e) => setSchedule({ ...schedule, end_time: e.target.value })}
                        className="bg-slate-950 border-slate-800 text-white h-12 rounded-none mt-2"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-300">Slot length</Label>
                      <select
                        data-testid="schedule-duration"
                        value={schedule.slot_duration_minutes}
                        onChange={(e) => setSchedule({ ...schedule, slot_duration_minutes: Number(e.target.value) })}
                        className="w-full bg-slate-950 border border-slate-800 text-white h-12 rounded-none mt-2 px-3"
                      >
                        <option value={30}>30 min</option>
                        <option value={60}>60 min</option>
                        <option value={90}>90 min</option>
                      </select>
                    </div>
                    <div>
                      <Label className="text-slate-300">Weeks ahead</Label>
                      <select
                        data-testid="schedule-weeks-ahead"
                        value={schedule.weeks_ahead}
                        onChange={(e) => setSchedule({ ...schedule, weeks_ahead: Number(e.target.value) })}
                        className="w-full bg-slate-950 border border-slate-800 text-white h-12 rounded-none mt-2 px-3"
                      >
                        {[4, 6, 8, 12, 16].map((w) => (
                          <option key={w} value={w}>{w} weeks</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <Label className="text-slate-300">Your timezone</Label>
                    <select
                      data-testid="schedule-timezone"
                      value={schedule.timezone || BROWSER_TZ}
                      onChange={(e) => setSchedule({ ...schedule, timezone: e.target.value })}
                      className="w-full sm:max-w-xs bg-slate-950 border border-slate-800 text-white h-12 rounded-none mt-2 px-3"
                    >
                      {TZ_OPTIONS.map((tz) => (
                        <option key={tz} value={tz}>{tz.replace(/_/g, " ")}</option>
                      ))}
                    </select>
                    <p className="text-slate-500 text-xs mt-2">
                      Your hours above are read in this zone — daylight saving is handled for you.
                    </p>
                  </div>
                </div>

                <div className="flex flex-col justify-end">
                  <Button
                    data-testid="save-schedule-btn"
                    onClick={saveSchedule}
                    disabled={savingSchedule}
                    className="bg-[#D4AF37] text-slate-950 hover:bg-[#B5952F] rounded-none h-12 px-8 uppercase tracking-widest text-xs"
                  >
                    {savingSchedule ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                    Save & fill calendar
                  </Button>
                  <p className="text-slate-600 text-xs mt-3 max-w-[220px]">
                    Existing and booked slots are never touched — only missing ones are added.
                  </p>
                </div>
              </div>
            </div>

            <div className="grid lg:grid-cols-2 gap-8">
              {/* Create Slots */}
              <div className="bg-slate-900/50 border border-slate-800 p-6">
                <h2 className="font-serif text-xl text-white mb-6 flex items-center gap-2">
                  <Plus className="w-5 h-5 text-[#D4AF37]" />
                  Create Availability
                </h2>

                <div className="space-y-6">
                  <div>
                    <Label className="text-slate-300 mb-2 block">Select Dates</Label>
                    <Calendar
                      mode="multiple"
                      selected={selectedDates}
                      onSelect={setSelectedDates}
                      disabled={(date) => date < new Date()}
                      className="bg-slate-900 rounded-none border border-slate-800"
                    />
                    <p className="text-slate-500 text-xs mt-2">
                      {selectedDates.length} date(s) selected
                    </p>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-slate-300">Start Time</Label>
                      <Input
                        type="time"
                        data-testid="start-time-input"
                        value={startTime}
                        onChange={(e) => setStartTime(e.target.value)}
                        className="bg-slate-950 border-slate-800 text-white h-12 rounded-none mt-2"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-300">End Time</Label>
                      <Input
                        type="time"
                        data-testid="end-time-input"
                        value={endTime}
                        onChange={(e) => setEndTime(e.target.value)}
                        className="bg-slate-950 border-slate-800 text-white h-12 rounded-none mt-2"
                      />
                    </div>
                  </div>

                  <div>
                    <Label className="text-slate-300">Slot Duration (minutes)</Label>
                    <select
                      data-testid="slot-duration-select"
                      value={slotDuration}
                      onChange={(e) => setSlotDuration(Number(e.target.value))}
                      className="w-full bg-slate-950 border border-slate-800 text-white h-12 rounded-none mt-2 px-3"
                    >
                      <option value={30}>30 minutes</option>
                      <option value={60}>60 minutes</option>
                      <option value={90}>90 minutes</option>
                    </select>
                  </div>

                  <Button
                    data-testid="create-slots-btn"
                    onClick={handleCreateSlots}
                    disabled={loading || selectedDates.length === 0}
                    className="w-full bg-[#D4AF37] text-slate-950 hover:bg-[#B5952F] rounded-none h-12"
                  >
                    {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                    Create Slots
                  </Button>
                </div>

                {/* Quick Add */}
                <div className="mt-6 pt-6 border-t border-slate-800">
                  <p className="text-slate-400 text-sm mb-3">Quick Add:</p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const dates = [];
                        for (let i = 0; i < 7; i++) {
                          const d = addDays(new Date(), i);
                          if (d.getDay() !== 0 && d.getDay() !== 6) dates.push(d);
                        }
                        setSelectedDates(dates);
                      }}
                      className="border-slate-700 text-slate-300 hover:bg-slate-800 rounded-none text-xs"
                    >
                      Next 5 Weekdays
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const dates = [];
                        for (let i = 0; i < 14; i++) {
                          const d = addDays(new Date(), i);
                          if (d.getDay() !== 0 && d.getDay() !== 6) dates.push(d);
                        }
                        setSelectedDates(dates);
                      }}
                      className="border-slate-700 text-slate-300 hover:bg-slate-800 rounded-none text-xs"
                    >
                      Next 2 Weeks
                    </Button>
                  </div>
                </div>
              </div>

              {/* Existing Slots */}
              <div className="bg-slate-900/50 border border-slate-800 p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="font-serif text-xl text-white flex items-center gap-2">
                    <Clock className="w-5 h-5 text-[#D4AF37]" />
                    Existing Slots
                  </h2>
                  {availabilitySlots.length > 0 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleDeleteAllSlots}
                      className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                    >
                      <Trash2 className="w-4 h-4 mr-1" />
                      Clear All
                    </Button>
                  )}
                </div>

                {Object.keys(slotsByDate).length === 0 ? (
                  <div className="text-center py-12 border border-dashed border-slate-700">
                    <Clock className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                    <p className="text-slate-400">No availability slots created yet.</p>
                  </div>
                ) : (
                  <div className="space-y-4 max-h-[500px] overflow-y-auto pr-2">
                    {Object.entries(slotsByDate).sort().map(([date, slots]) => (
                      <div key={date} className="border border-slate-800 p-4">
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="text-white font-medium">{date}</h4>
                          {slots.some((s) => !s.is_booked) && (
                            <button
                              onClick={() => handleClearDay(date)}
                              data-testid={`clear-day-${date}`}
                              className="text-red-400 hover:text-red-300 text-[10px] uppercase tracking-widest inline-flex items-center gap-1 transition-colors"
                            >
                              <Trash2 className="w-3.5 h-3.5" /> Clear day
                            </button>
                          )}
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {slots.map((slot) => (
                            <div
                              key={slot.id}
                              className={`flex items-center gap-2 px-3 py-1 text-sm border ${
                                slot.is_booked 
                                  ? 'border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37]' 
                                  : 'border-slate-700 text-slate-400'
                              }`}
                            >
                              <span>{slot.local_start_time || slot.start_time}</span>
                              {slot.is_booked && <Check className="w-3 h-3" />}
                              {!slot.is_booked && (
                                <button
                                  onClick={() => handleDeleteSlot(slot.id)}
                                  className="text-slate-500 hover:text-red-400 ml-1"
                                >
                                  <X className="w-3 h-3" />
                                </button>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </TabsContent>

          {/* Bookings Tab */}
          <TabsContent value="bookings">
            <AdminBookingsTab
              bookings={bookings}
              onOpenBrief={toggleBrief}
              briefs={briefs}
              briefLoading={briefLoading}
              openBrief={openBrief}
              generateBrief={generateBrief}
              caseFileLink={caseFileLink}
              caseFileLinks={caseFileLinks}
              caseFileLoading={caseFileLoading}
              SessionBriefRow={SessionBriefRow}
              onRefresh={fetchData}
            />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminPanel;
